// Deterministic scoring for the anchored core.
//
// Two learners who give the same answers get the same scores. That sentence is
// the whole point: it is what makes reliability computable, and it is what the
// model-authored scoring could never provide.
//
// No model call happens here. Scoring is a pure function of the item bank and the
// learner's option ids — which also removes a Claude call from the scoring path
// (intent §Constraints, cost).

import type {
  AnchorItemResponse,
  CogniPrintProfile,
  DiagnosticDimensionKey,
  DimensionProvenance,
} from '../types/cogniprint';
import { PAID_DIMENSION_KEYS } from '../types/cogniprint';
import { ANCHOR_SET_VERSION, DIMENSION_SPECS, anchorItemById, anchorItemsForMode } from './anchor-items';

/**
 * Minimum scored items before a dimension counts as assessed. Below this the
 * dimension is `defaulted`: a neutral value stands in, and nothing renders it as
 * a finding or adapts on it.
 */
export const MIN_ITEMS_FOR_ASSESSED = 2;

/** Confidence rises with evidence and falls with disagreement between items. */
const CONFIDENCE_BASE = 0.25;
const CONFIDENCE_PER_ITEM = 0.12;
/** A self-report instrument does not get to be certain. */
export const MAX_SELF_REPORT_CONFIDENCE = 0.85;
/** The ceiling for a dimension we had to stand in for. */
export const DEFAULTED_CONFIDENCE_CEILING = 0.35;

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Scale a set of weights to sum to exactly 100, fixing rounding drift on the largest. */
function normaliseTo100(weights: Record<string, number>): Record<string, number> {
  const entries = Object.entries(weights);
  const sum = entries.reduce((a, [, v]) => a + Math.max(0, v), 0);
  if (sum === 0) return Object.fromEntries(entries.map(([k]) => [k, Math.round(100 / entries.length)]));
  const scaled = entries.map(([k, v]) => [k, Math.round((Math.max(0, v) / sum) * 100)] as [string, number]);
  const drift = 100 - scaled.reduce((a, [, v]) => a + v, 0);
  if (drift !== 0) {
    let maxIdx = 0;
    for (let i = 1; i < scaled.length; i++) if (scaled[i][1] > scaled[maxIdx][1]) maxIdx = i;
    scaled[maxIdx][1] += drift;
  }
  return Object.fromEntries(scaled);
}

/** Population standard deviation, used as the disagreement penalty. */
function stdev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export interface ScoredDimension {
  /** Subscale values, keyed as the profile type expects them. */
  values: Record<string, number>;
  confidence: number;
  provenance: DimensionProvenance;
  scoredItemCount: number;
}

/**
 * Score one dimension from the learner's responses.
 *
 * A dimension the learner was never shown comes back `not_assessed`. One they were
 * shown but skipped past comes back `defaulted`. Neither is a finding.
 */
export function scoreDimension(
  dimension: DiagnosticDimensionKey,
  responses: AnchorItemResponse[],
  wasPresented: boolean,
): ScoredDimension {
  const spec = DIMENSION_SPECS[dimension];
  const scored = responses.filter(
    (r) => r.dimension === dimension && !r.skipped && r.optionId != null,
  );

  const contributions: Array<Record<string, number>> = [];
  for (const response of scored) {
    const item = anchorItemById(response.itemId);
    const option = item?.options.find((o) => o.id === response.optionId);
    if (item && option) contributions.push(option.contributions);
  }

  if (contributions.length < MIN_ITEMS_FOR_ASSESSED) {
    return {
      values: { ...spec.neutral },
      confidence: contributions.length === 0 ? 0 : DEFAULTED_CONFIDENCE_CEILING / 2,
      provenance: wasPresented ? 'defaulted' : 'not_assessed',
      scoredItemCount: contributions.length,
    };
  }

  let values: Record<string, number>;
  const perKeySeries: Record<string, number[]> = {};
  for (const key of spec.keys) perKeySeries[key] = [];

  if (spec.combine === 'sum_normalise') {
    const totals: Record<string, number> = Object.fromEntries(spec.keys.map((k) => [k, 0]));
    for (const c of contributions) {
      for (const key of spec.keys) {
        const v = c[key] ?? 0;
        totals[key] += v;
        perKeySeries[key].push(v);
      }
    }
    values = normaliseTo100(totals);
  } else {
    values = {};
    for (const key of spec.keys) {
      const series = contributions
        .map((c) => c[key])
        .filter((v): v is number => typeof v === 'number');
      perKeySeries[key] = series;
      values[key] = series.length
        ? clamp(Math.round(series.reduce((a, b) => a + b, 0) / series.length), 0, 100)
        : spec.neutral[key];
    }
  }

  // Confidence: more items raise it, disagreement between them lowers it. The
  // dispersion term is what stops four contradictory answers reading as certainty.
  const dispersion =
    spec.keys.reduce((worst, key) => Math.max(worst, stdev(perKeySeries[key] ?? [])), 0) / 50;
  const raw =
    CONFIDENCE_BASE + CONFIDENCE_PER_ITEM * contributions.length - 0.25 * clamp(dispersion, 0, 1);

  return {
    values,
    confidence: round(clamp(raw, 0.1, MAX_SELF_REPORT_CONFIDENCE)),
    provenance: 'anchored',
    scoredItemCount: contributions.length,
  };
}

export interface ScoredProfileParts {
  contentFormat: CogniPrintProfile['contentFormat'];
  learningApproach: CogniPrintProfile['learningApproach'];
  depthTolerance: CogniPrintProfile['depthTolerance'];
  sequencing: CogniPrintProfile['sequencing'];
  infoDensity: CogniPrintProfile['infoDensity'];
  motivation: CogniPrintProfile['motivation'];
  viewpointRange: CogniPrintProfile['viewpointRange'];
  dimensionProvenance: Partial<Record<DiagnosticDimensionKey, DimensionProvenance>>;
  questionsAsked: number;
  overallConfidence: number;
  anchorSetVersion: string;
}

/**
 * Build the scoreable half of a profile from a set of anchor responses.
 *
 * Pure and deterministic: identical responses in, identical scores out.
 */
export function scoreAnchorResponses(
  responses: AnchorItemResponse[],
  mode: 'quick' | 'full',
): ScoredProfileParts {
  const presented = new Set(anchorItemsForMode(mode).map((i) => i.dimension));

  const scored = {} as Record<DiagnosticDimensionKey, ScoredDimension>;
  for (const dimension of Object.keys(DIMENSION_SPECS) as DiagnosticDimensionKey[]) {
    scored[dimension] = scoreDimension(dimension, responses, presented.has(dimension));
  }

  const dimensionProvenance: Partial<Record<DiagnosticDimensionKey, DimensionProvenance>> = {};
  for (const [dimension, result] of Object.entries(scored) as Array<[DiagnosticDimensionKey, ScoredDimension]>) {
    dimensionProvenance[dimension] = result.provenance;
  }

  // A paid dimension that was never presented is stored as null — the type's way
  // of saying "not assessed". A `defaulted` one is stored as null too: a neutral
  // stand-in must not masquerade as a measurement just because it has a number.
  const paidOrNull = (key: 'infoDensity' | 'viewpointRange') =>
    scored[key].provenance === 'anchored'
      ? { score: scored[key].values.score, confidence: scored[key].confidence }
      : null;

  const parts: ScoredProfileParts = {
    contentFormat: {
      score: scored.contentFormat.values as unknown as CogniPrintProfile['contentFormat']['score'],
      confidence: scored.contentFormat.confidence,
    },
    learningApproach: {
      score: scored.learningApproach.values as unknown as CogniPrintProfile['learningApproach']['score'],
      confidence: scored.learningApproach.confidence,
    },
    depthTolerance: {
      score: scored.depthTolerance.values.score,
      confidence: scored.depthTolerance.confidence,
    },
    sequencing: {
      score: scored.sequencing.values as unknown as CogniPrintProfile['sequencing']['score'],
      confidence: scored.sequencing.confidence,
    },
    infoDensity: paidOrNull('infoDensity'),
    motivation:
      scored.motivation.provenance === 'anchored'
        ? {
            score: scored.motivation.values as unknown as NonNullable<CogniPrintProfile['motivation']>['score'],
            confidence: scored.motivation.confidence,
          }
        : null,
    viewpointRange: paidOrNull('viewpointRange'),
    dimensionProvenance,
    questionsAsked: responses.filter((r) => !r.skipped && r.optionId != null).length,
    overallConfidence: 0,
    anchorSetVersion: ANCHOR_SET_VERSION,
  };

  const contributing = [
    parts.contentFormat.confidence,
    parts.learningApproach.confidence,
    parts.depthTolerance.confidence,
    parts.sequencing.confidence,
    ...PAID_DIMENSION_KEYS.map((k) => parts[k as 'infoDensity' | 'motivation' | 'viewpointRange']?.confidence)
      .filter((c): c is number => typeof c === 'number'),
  ];
  parts.overallConfidence = round(
    contributing.reduce((a, b) => a + b, 0) / Math.max(1, contributing.length),
  );

  return parts;
}
